import { WhatsappTemplateComponent } from '@/shared/types/broadcast';
import { Attachment, Context } from '@/shared/types/chat';
import { OptionBase } from '@/shared/types/common';
import { INSIGHT_TYPES } from '@/shared/types/insights';
import { IntegrationType } from '@/shared/types/integration';
import {
  AuthenticationDebug,
  Correction,
  DebugStep,
  Entity,
  ExternalUrl,
  Fragment,
  MessageReminder,
  ReactionType,
  ResponseMessage,
  ToolInvocation,
  TryItCollection,
} from '@/shared/types/tryIt';

export type Channel = IntegrationType;

export type UserInfo = {
  channel: Channel;
  user_id: string;
  display_name?: string;
  email?: string;
  city?: string;
  locale?: string;
  region?: string;
  latitude?: string;
  longitude?: string;
};

/** The unwrapped value of a single insight (before it's wrapped with reasoning metadata). */
export type InsightRawValue = string | number | boolean | Record<string, unknown> | null;

export type InsightType =
  'email' | 'phone' | 'string' | 'integer' | 'categorical' | 'boolean' | 'datetime';

/** An insight can be either a structured object with value+reasoning, or a legacy raw primitive. */
export type InsightValues =
  | { value: InsightRawValue; reasoning: string | null; type?: InsightType }
  | string
  | number
  | boolean
  | null;

export type Log = {
  brain_id: string;
  brain_parent_id: string;
  brain_version: number;
  channel: Channel;
  channel_user_id: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email?: string;
  desk_id: string;
  start_time: string;
  end_time: string;
  expired_time: string | null;
  external_user_id: string | null;
  integration_id: string | null;
  is_contained: boolean;
  is_covered: boolean;
  is_test: boolean;
  participated_agents: { agent_id: string; agent_name: string }[];
  participated_brains: { brain_id: string; brain_version: number; brain_parent_id: string }[];
  participated_language_models: {
    language_model_id: string | null;
    model_id: string;
    brain_parent_id: string;
  }[];
  rule_ids: string[];
  rating: 1 | 2 | 3 | 4 | 5;
  feedback: string;
  session_id: string;
  tags: string[] | null;
  thumbs_down: number;
  thumbs_up: number;
  total_user_messages: number;
  csat_brain: number | null;
  csat_human_agent: number | null;
  goal_achieved: string | null;
  user_sentiment: string | null;
  conversation_summary: string | null;
  insights?: Record<string, InsightValues> | null;
};

export type LogSessionContentRow = { messages: LogMessage[] } & Log;

export type LogMessage = {
  /** The channel this event happened on, so a replay can show where the session moved. */
  channel?: Channel;
  integration_id?: string | null;
  time: string;
  tags: string[] | null;
  context: Context | null;
  agent_id: string | null;
  request_id: string;
  first_message: boolean;
  response_time: number | null;
  brain_id: string | null;
  brain_parent_id: string | null;
  rule_ids: string[] | null;
  brain_version: number | null;
  collection_id: string | null;
  collection_request_code: number | null;
  collection_response_code: number | null;
  collection_sources: Fragment[] | null;
  new_brain_session: boolean | null;
  corrections?: Correction[];
  external_urls?: ExternalUrl[];
  reaction: ReactionType;
} & (
  | {
      event: 'message:brain_send';
      message: {
        debug: {
          steps: DebugStep[];
          logs: string[];
          nodes_stack: {
            node_id: string;
            name: string;
            type: 'intent' | 'event';
            request_id: string;
          }[];
          standalone_question?: string;
          corrections?: Correction[];
          collection?: TryItCollection;
          external_urls?: ExternalUrl[];
          tools_invoked?: ToolInvocation[];
          authentication?: AuthenticationDebug;
        };
        intents: { intent: string; confidence: number }[];
        entities: Entity[];
        responses: ResponseMessage[];
        collection?: TryItCollection;
        planner_intents?: string[];
        processed_text?: string;
        reminder?: MessageReminder;
      };
    }
  | {
      event: 'message:broadcast_send';
      message: {
        debug: {
          broadcast: { broadcast_id: string; external_message_id: string; broadcast_name: string };
        };
        responses: ResponseMessage[];
      };
    }
  | {
      event: 'message:campaign_send';
      message: {
        debug: {
          campaign: {
            campaign_id: string;
            external_message_id: string;
            campaign_name: string;
            sender_id: string;
          };
        };
        responses: ResponseMessage[];
      };
    }
  | {
      event:
        | 'message:received'
        | 'message:send'
        | 'session:rating'
        | 'conversation:closed'
        | 'session:closed'
        | 'session:expired';
      message: {
        text: string;
        type: string;
        attachments?: Attachment[];
        body?: {
          template?: WhatsappTemplateComponent[];
          text?: string;
          attachments?: Attachment[];
        };
        reminder?: MessageReminder;
      } | null;
    }
);

export type InsightOperator =
  | 'eq'
  | 'ne'
  | 'lt'
  | 'gt'
  | 'lte'
  | 'gte'
  | 'between'
  | 'like'
  | 'nlike'
  | 'in'
  | 'nin'
  | 'exists';

export type InsightCategory = (typeof INSIGHT_TYPES)[keyof typeof INSIGHT_TYPES];

export interface InsightFilter {
  id: string;
  category: InsightCategory;
  operator: InsightOperator;
  values: (string | number | boolean)[];
}

export type LogsFilterType =
  | 'isCovered'
  | 'isTest'
  | 'isContained'
  | 'isMeaningful'
  | 'minNumUserMessages'
  | 'channels'
  | 'sessionId'
  | 'tags'
  | 'ratings'
  | 'maxConfidence'
  | 'userId'
  | 'channelUserId'
  | 'externalUserId'
  | 'languageModelIds'
  | 'reactions'
  | 'insightFilters'
  | 'environments'
  | 'hasCollection'
  | 'hasRecording'
  | 'hasVoicemail'
  | 'userName'
  | 'userEmail'
  | 'agentIds'
  | 'csatBrain'
  | 'goalAchieved'
  | 'userSentiment'
  | 'firstMessageType';

export type AccountAnalyticsFilterType =
  | 'isTest'
  | 'channels'
  | 'brainIds'
  | 'deskIds'
  | 'agentIds'
  | 'minNumUserMessages'
  | 'languageModelIds'
  | 'reactions'
  | 'hasRecording'
  | 'hasVoicemail'
  | 'insightFilters'
  | 'ratings'
  | 'csatBrain'
  | 'tags'
  | 'firstMessageType';

export type FirstMessageType = 'proactive' | 'reactive';

export type AccountAnalytics = {
  type: AccountAnalyticsFilterType;
  isTest?: boolean;
  channels?: string[];
  deskIds?: OptionBase[];
  brainIds?: OptionBase[];
  agentIds?: OptionBase[];
  minNumUserMessages?: number;
  insightFilters?: InsightFilter[];
  languageModelIds?: string[];
  reactions?: boolean;
  hasRecording?: boolean;
  hasVoicemail?: boolean;
  tags?: string[];
  tagsMode?: TagsMode;
  ratings?: (string | number)[];
  ratingsMode?: RatingsMode;
  csatBrain?: (string | number)[];
  csatBrainMode?: RatingsMode;
  firstMessageType?: FirstMessageType | null;
};

export type RatingsMode = 'in' | 'moreThan' | 'lessThan' | 'exact';

export type TagsMode = 'all' | 'any';

export const enum AnalyticsType {
  LOGS = 'logs',
  ACCOUNT = 'accountAnalytics',
}

export type LogsAnalytics = {
  type: LogsFilterType;
  isCovered?: boolean;
  isTest?: boolean;
  isContained?: boolean;
  isMeaningful?: boolean;
  minNumUserMessages?: string;
  channels?: string[];
  sessionId?: string;
  tags?: string[];
  tagsMode?: TagsMode;
  ratings?: string[];
  ratingsMode?: RatingsMode;
  maxConfidence?: string;
  userId?: string;
  channelUserId?: string;
  externalUserId?: string;
  insightFilters?: InsightFilter[];
  deskIds?: string[];
  hasCollection?: boolean;
  hasRecording?: boolean;
  hasVoicemail?: boolean;
  userName?: string;
  userEmail?: string;
  agentIds?: string[];
  languageModelIds?: string[];
  csatBrain?: number[];
  csatBrainMode?: RatingsMode;
  goalAchieved?: number[];
  userSentiment?: number[];
  reactions?: boolean | null;
  numUserMessagesOperator?: string;
  numUserMessagesValue?: number;
  firstMessageType?: FirstMessageType | null;
};

export type CallRecordingStatus = 'in-progress' | 'completed' | 'failed';

export interface CallRecording {
  account_id: string;
  session_id: string;
  recording_sid: string;
  recording_status: CallRecordingStatus;
  recording_url: string;
  recording_duration?: number;
  created: string;
  updated: string;
  // Narrow view of the JSONB metadata column: only the audio format the route
  // needs. The full blob is not exposed.
  metadata?: { recording_format?: string };
}

export type Analytics = {
  endDate?: string | null;
  startDate?: string | null;
  lastSeen?: string[];
  accountAnalytics?: AccountAnalytics[];
  logs?: LogsAnalytics[];
  filtersLoaded?: boolean;
  logsFiltersPanel: LogsFiltersPanelState;
};

export type LogsFiltersPanelState = {
  openFilter: string | null;
  openFilterIndex: number | null;
  dropdownOpen: boolean;
  openSource: 'menu' | 'pill' | null;
};

export type QueryVariables = {
  endDate?: string | null;
  startDate?: string | null;
  channels?: string | null;
  offset?: number;
};

export type AnalyticsQueryVariables = {
  limit?: number;
  brainIds: string | null;
  isTest?: boolean | null;
  insightFilters?: InsightFilter[] | null;
} & QueryVariables;

export type LogsQueryVariables = {
  limit?: number;
  deskIds?: string | null;
  brainIds?: string | null;
  integrationIds?: string | null;
  brainVersions?: string | null;
  dateRange?: string;
  minNumUserMessages?: number | null;
  maxConfidence?: number | null;
  ratings?: string | null;
  tags?: string | null;
  tagsMatch?: TagsMode;
  sessionId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  channelUserId?: string | null;
  externalUserId?: string | null;
  isContained?: boolean | null;
  isCovered?: boolean | null;
  isMeaningful?: boolean | null;
  isTest?: boolean | null;
  isProactive?: boolean | null;
  hasRecording?: boolean | null;
  hasVoicemail?: boolean | null;
  agentIds?: string | null;
  accountId?: string | null;
  insightFilters?: InsightFilter[] | null;
} & QueryVariables;

// Filter Views for saving and loading filter configurations
export interface FilterViewListItem {
  id: string;
  name: string;
}

export interface FilterView extends FilterViewListItem {
  filters: LogsAnalytics[];
}

export interface BrainFilterViews {
  views: FilterView[];
  activeViewId?: string; // Currently loaded view ID (only used locally, not stored)
}

export interface AccountFilterView extends FilterViewListItem {
  filters: unknown[];
}
